/**
 * BF2 product gallery: variant-scoped image filtering.
 *
 * Reuses the theme's Product/Variants JS for variant selection + cart. This
 * only owns the visible gallery. On every variant change it shows the selected
 * variant's image block plus the global images (leading, unassigned) and hides
 * the other variants' blocks. See snippets/bf2-product-gallery.liquid for how
 * each media's data-block is computed (a block starts at each variant-assigned
 * image; leading unassigned images are "global").
 *
 * Global thumbs are pushed behind the variant thumbs via CSS order, so even
 * though they come first in the media list they display after.
 *
 * No-op on pages without a bf2 gallery.
 */

import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('bf2-gallery', false);

function initGallery(section) {
  const gallery = section.querySelector('[data-bf2-gallery]');
  if (!gallery || gallery._bf2Bound) return;
  gallery._bf2Bound = true;

  const slides = Array.from(gallery.querySelectorAll('[data-bf2-slide]'));
  const thumbs = Array.from(gallery.querySelectorAll('[data-bf2-thumb]'));
  if (thumbs.length === 0) return;

  const setActive = (index) => {
    const idx = String(index);
    slides.forEach((s) => s.classList.toggle('is-active', s.dataset.index === idx));
    thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.index === idx));
  };

  // Thumbnail click swaps the main image.
  thumbs.forEach((t) => t.addEventListener('click', () => setActive(t.dataset.index)));

  const filterToVariant = (variant) => {
    const featuredId =
      variant && variant.featured_media ? String(variant.featured_media.id) : null;

    thumbs.forEach((t) => {
      const block = t.dataset.block;
      const show = block === 'global' || (featuredId && block === featuredId);
      t.style.display = show ? '' : 'none';
    });

    // Prefer the variant's own featured image; else the first visible non-global
    // (variant block) thumb; else the first visible thumb.
    let target = featuredId
      ? thumbs.find((t) => t.dataset.mediaId === featuredId)
      : null;
    if (!target || target.style.display === 'none') {
      target =
        thumbs.find((t) => t.style.display !== 'none' && t.dataset.block !== 'global') ||
        thumbs.find((t) => t.style.display !== 'none');
    }
    if (target) setActive(target.dataset.index);
  };

  // Filter to the initially selected variant on load.
  const initialVariant = () => {
    const jsonEl = section.querySelector('[data-variant-json]');
    const selectEl = section.querySelector('[data-product-select]');
    if (!jsonEl) return null;
    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent || jsonEl.innerHTML);
    } catch (e) {
      debug.error('variant json parse failed', e);
      return null;
    }
    if (!Array.isArray(variants) || variants.length === 0) return null;
    const id = selectEl && selectEl.value;
    return variants.find((v) => String(v.id) === String(id)) || variants[0];
  };

  const start = initialVariant();
  if (start) filterToVariant(start);

  // The theme dispatches variantChange on the [data-section-type="product"]
  // container on every option change.
  section.addEventListener('variantChange', (event) => {
    if (!event.detail || !event.detail.variant) return;
    filterToVariant(event.detail.variant);
  });

  debug.log('gallery wired', { slides: slides.length, thumbs: thumbs.length });
}

function init(root = document) {
  root.querySelectorAll('[data-section-type="product"]').forEach(initGallery);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => init(event.target));
